import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Bot, X, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Question } from "@/lib/questionLoader";
import { account } from "@/lib/appwrite";

// Simple markdown renderer for Aira chat messages
export function AiraMarkdown({ content }: { content: string }) {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  const parseInline = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    const regex = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let last = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > last) parts.push(text.slice(last, match.index));
      const raw = match[0];
      if (raw.startsWith("**"))
        parts.push(<strong key={key++}>{raw.slice(2, -2)}</strong>);
      else if (raw.startsWith("`"))
        parts.push(
          <code key={key++} className="bg-black/10 rounded px-1 font-mono text-xs">
            {raw.slice(1, -1)}
          </code>
        );
      else parts.push(<em key={key++}>{raw.slice(1, -1)}</em>);
      last = match.index + raw.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i]!;

    if (line.startsWith("```")) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.startsWith("```")) {
        codeLines.push(lines[i]!);
        i++;
      }
      elements.push(
        <pre key={i} className="bg-black/10 rounded p-2 text-xs font-mono overflow-auto my-1 whitespace-pre-wrap">
          {codeLines.join("\n")}
        </pre>
      );
    } else if (/^[-*] /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*] /.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^[-*] /, ""));
        i++;
      }
      elements.push(
        <ul key={i} className="list-disc list-inside space-y-0.5 my-1">
          {items.map((item, j) => <li key={j}>{parseInline(item)}</li>)}
        </ul>
      );
      continue;
    } else if (/^\d+\. /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^\d+\. /, ""));
        i++;
      }
      elements.push(
        <ol key={i} className="list-decimal list-inside space-y-0.5 my-1">
          {items.map((item, j) => <li key={j}>{parseInline(item)}</li>)}
        </ol>
      );
      continue;
    } else if (line.trim() === "") {
      elements.push(<div key={i} className="h-1.5" />);
    } else {
      elements.push(<p key={i}>{parseInline(line)}</p>);
    }
    i++;
  }

  return <div className="space-y-0.5">{elements}</div>;
}

const CHAR_LIMIT = 3000;

export function AiraPanel({
  isOpen,
  onClose,
  topic,
  question,
  selectedAnswer,
  isChecked,
}: {
  isOpen: boolean;
  onClose: () => void;
  topic: string;
  question: Question;
  selectedAnswer?: string | null;
  isChecked: boolean;
}) {
  const [messages, setMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [width, setWidth] = useState(384);
  const [isDragging, setIsDragging] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const newWidth = e.clientX;
      setWidth(Math.min(Math.max(newWidth, 280), 700));
    };
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  useEffect(() => {
    setMessages([]);
    setInput("");
  }, [question.id]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    const newMessages: { role: "user" | "assistant"; content: string }[] = [
      ...messages,
      { role: "user", content: userMessage },
    ];
    setMessages(newMessages);
    setIsLoading(true);

    const correctOption = question.options.find((o) => o.isCorrect);
    const selectedOption = question.options.find((o) => o.id === selectedAnswer);

    try {
      const { jwt } = await account.createJWT();
      const res = await fetch("/api/aira", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          messages: newMessages,
          topic,
          question: question.question,
          options: question.options.map((o) => `${o.id}: ${o.text}`).join("\n"),
          selectedAnswer: selectedOption ? `${selectedOption.id}: ${selectedOption.text}` : null,
          correctAnswer: isChecked && correctOption ? `${correctOption.id}: ${correctOption.text}` : null,
          explanation: isChecked ? question.explanation : null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || "Request failed");
      }
      if (!res.body) throw new Error("No response body");

      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);
      setIsLoading(false);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = { ...last, content: last.content + chunk };
          }
          return updated;
        });
      }
    } catch {
      setIsLoading(false);
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, I'm having trouble connecting. Please try again." },
      ]);
    }
  };

  if (!isOpen) return null;

  const dots = (
    <span className="inline-flex gap-1 items-center">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </span>
  );

  return (
    <div
      className="fixed left-0 top-0 bottom-0 z-50 bg-background border-r border-border flex flex-col shadow-2xl animate-in slide-in-from-left duration-200"
      style={{ width, cursor: isDragging ? "col-resize" : undefined }}
    >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border shrink-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Aira</p>
            <p className="text-xs text-muted-foreground truncate capitalize">
              AI Tutor · {topic.replace(/_/g, " ")}
            </p>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Bot className="h-6 w-6 text-white" />
              </div>
              <div>
                <p className="font-semibold">Hi, I'm Aira!</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Ask me anything about this{" "}
                  <span className="capitalize">{topic.replace(/_/g, " ")}</span>{" "}
                  question — I'll help you understand, not just memorize.
                </p>
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div
              key={i}
              className={cn("flex gap-2", msg.role === "user" ? "flex-row-reverse" : "flex-row")}
            >
              {msg.role === "assistant" && (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0 mt-1">
                  <Bot className="h-3 w-3 text-white" />
                </div>
              )}
              <div
                className={cn(
                  "max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed",
                  msg.role === "user"
                    ? "bg-primary text-primary-foreground rounded-tr-sm"
                    : "bg-muted rounded-tl-sm"
                )}
              >
                {msg.content === "" && msg.role === "assistant"
                  ? dots
                  : msg.role === "assistant"
                  ? <AiraMarkdown content={msg.content} />
                  : msg.content}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex gap-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0 mt-1">
                <Bot className="h-3 w-3 text-white" />
              </div>
              <div className="bg-muted rounded-2xl rounded-tl-sm px-3 py-2">{dots}</div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-3 border-t border-border shrink-0">
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Ask Aira about this question…"
              rows={1}
              disabled={isLoading}
              className={cn(
                "flex-1 resize-none rounded-xl border bg-muted/50 px-3 py-2 text-sm focus:outline-none focus:ring-2 min-h-[40px] max-h-[120px] overflow-auto",
                input.length > CHAR_LIMIT
                  ? "border-red-400 focus:ring-red-300"
                  : "border-border focus:ring-violet-400/50"
              )}
            />
            <Button
              size="icon"
              disabled={!input.trim() || isLoading || input.length > CHAR_LIMIT}
              onClick={sendMessage}
              className="h-10 w-10 rounded-xl shrink-0 bg-gradient-to-br from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white border-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center justify-between mt-1.5 px-0.5">
            <p className="text-[10px] text-muted-foreground">
              Aira only discusses questions related to this topic
            </p>
            {input.trim() && (
              <p className={cn(
                "text-[10px] font-medium",
                input.length > CHAR_LIMIT ? "text-red-500" : "text-muted-foreground"
              )}>
                {input.length}/{CHAR_LIMIT}
              </p>
            )}
          </div>
        </div>

        {/* Resize handle */}
        <div
          className="absolute right-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/20 transition-colors group"
          onMouseDown={(e) => { e.preventDefault(); setIsDragging(true); }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-muted-foreground/30 group-hover:bg-primary/50 transition-colors" />
        </div>
    </div>
  );
}
